import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, User, LogOut, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { CountrySelector } from "@/components/CountrySelector";

export const Navbar = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success(t('toast.logoutSuccess'));
  };

  // Filter out "Become an Expert" if user is already an expert
  const navLinks = [
    { href: "/experts", label: t('nav.browseExperts') },
    { href: "/enterprise", label: t('nav.enterprise') },
    ...(!user?.is_expert ? [{ href: "/become-expert", label: t('nav.becomeExpert') }] : []),
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 w-full z-50 bg-obsidian/80 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 sm:h-18 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0" data-testid="logo">
            <span className="font-display text-lg sm:text-xl lg:text-2xl text-gold-gradient">ExpertBase</span>
          </Link>

          {/* Desktop Navigation - Only show on large screens (1024px+) */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive(link.href)
                    ? "text-gold-500"
                    : "text-slate-400 hover:text-white"
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop Auth + Country Selector - Only show on large screens */}
          <div className="hidden lg:flex items-center gap-3">
            <CountrySelector variant="navbar" />
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2 text-slate-300 hover:text-white"
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-8 h-8 bg-gold-500/20 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-gold-500" />
                    </div>
                    <span className="max-w-[120px] truncate">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-charcoal border-slate-800" align="end">
                  <DropdownMenuItem 
                    onClick={() => navigate(user.is_expert ? "/expert-dashboard" : "/dashboard")}
                    className="cursor-pointer"
                    data-testid="dashboard-link"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    {t('nav.dashboard')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-slate-800" />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="cursor-pointer text-red-400 focus:text-red-400"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('nav.signOut')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate("/login")}
                  className="text-slate-400 hover:text-white"
                  data-testid="login-nav-btn"
                >
                  {t('nav.signIn')}
                </Button>
                <Button 
                  onClick={() => navigate("/register")}
                  className="bg-gold-500 hover:bg-gold-400 text-black font-medium rounded-sm"
                  data-testid="register-nav-btn"
                >
                  {t('nav.getStarted')}
                </Button>
              </>
            )}
          </div>

          {/* Mobile/Tablet: Country Selector + Menu Button - Show below lg (1024px) */}
          <div className="lg:hidden flex items-center gap-2 sm:gap-3">
            <CountrySelector variant="navbar" />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-slate-400 hover:text-white transition-colors"
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5 sm:w-6 sm:h-6" /> : <Menu className="w-5 h-5 sm:w-6 sm:h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile/Tablet Menu - Full width slide down */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 sm:py-6 border-t border-slate-800 animate-in slide-in-from-top-2 duration-200">
            <div className="flex flex-col gap-2 sm:gap-3">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`text-base sm:text-lg font-medium py-2 px-3 rounded-sm transition-colors ${
                    isActive(link.href) 
                      ? "text-gold-500 bg-gold-500/10" 
                      : "text-slate-400 hover:text-white hover:bg-slate-800/50"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              
              <div className="pt-4 mt-2 border-t border-slate-800 flex flex-col gap-3">
                {user ? (
                  <>
                    {/* User Info */}
                    <div className="flex items-center gap-3 px-3 py-2 mb-2">
                      <div className="w-10 h-10 bg-gold-500/20 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-gold-500" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{user.name}</p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </div>
                    </div>
                    <Button 
                      onClick={() => {
                        navigate(user.is_expert ? "/expert-dashboard" : "/dashboard");
                        setMobileMenuOpen(false);
                      }}
                      variant="outline"
                      className="border-slate-700 justify-start h-12 text-base"
                    >
                      <LayoutDashboard className="w-5 h-5 mr-3" />
                      {t('nav.dashboard')}
                    </Button>
                    <Button 
                      onClick={() => {
                        handleLogout();
                        setMobileMenuOpen(false);
                      }}
                      variant="ghost"
                      className="text-red-400 justify-start h-12 text-base hover:bg-red-500/10"
                    >
                      <LogOut className="w-5 h-5 mr-3" />
                      {t('nav.signOut')}
                    </Button>
                  </>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button 
                      onClick={() => {
                        navigate("/login");
                        setMobileMenuOpen(false);
                      }}
                      variant="outline"
                      className="border-slate-700 h-12 text-base flex-1"
                    >
                      {t('nav.signIn')}
                    </Button>
                    <Button 
                      onClick={() => {
                        navigate("/register");
                        setMobileMenuOpen(false);
                      }}
                      className="bg-gold-500 hover:bg-gold-400 text-black h-12 text-base font-medium flex-1"
                    >
                      {t('nav.getStarted')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
